/**
 * HEADER SEARCH INLINE — Fabletics GT
 *
 * Barra de búsqueda siempre abierta en el centro del header. Al escribir trae
 * los resultados predictivos al panel de abajo, usando el endpoint que ya usa
 * <search-modal>: routes.predictive_search_url + section_id=predictive-search,
 * que devuelve el HTML de sections/predictive-search.liquid ya renderizado.
 *
 * Solo se muestra en desktop (>= 1150px). Por debajo el elemento está oculto
 * por CSS y el icono del header abre #search-modal como siempre.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;

  class HeaderSearchInline extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.panel = this.querySelector('[data-search-panel]');
      this.results = this.querySelector('[data-search-results]');
      this.skeleton = this.querySelector('[data-search-skeleton]');
      this.status = this.querySelector('[data-search-status]');

      if (!this.form || !this.input) return;

      this.debounceTimer = null;
      this.controller = null;

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

      this.input.addEventListener('input', this.onInput.bind(this));
      /* Al volver a enfocar un campo con texto, reabrir los resultados */
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.clearBtn.addEventListener('click', this.clear.bind(this));

      /* No enviar búsquedas vacías: el form iría a /search sin query */
      this.form.addEventListener('submit', (event) => {
        if (this.input.value.trim() === '') event.preventDefault();
      });

      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      clearTimeout(this.debounceTimer);
      if (this.controller) this.controller.abort();
    }

    clear() {
      this.input.value = '';
      this.hidePanel();
      this.input.focus();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      /* Escape limpia; el campo no se cierra porque es permanente */
      this.clear();
    }

    onFocus() {
      var term = this.input.value.trim();
      if (term.length >= MIN_CHARS) this.search(term);
    }

    onDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.hidePanel();
    }

    /* Si el foco sale del componente por teclado, ocultar el panel pero
       conservar lo escrito. */
    onFocusOut() {
      requestAnimationFrame(() => {
        if (this.contains(document.activeElement)) return;
        this.hidePanel();
      });
    }

    onInput() {
      var term = this.input.value.trim();
      clearTimeout(this.debounceTimer);

      if (term.length < MIN_CHARS) {
        if (this.controller) this.controller.abort();
        this.hidePanel();
        return;
      }
      this.debounceTimer = setTimeout(() => this.search(term), DEBOUNCE_MS);
    }

    searchUrl(term) {
      var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      var url = new URL(base, window.location.origin);
      url.searchParams.set('q', term);
      /* Solo productos: el desplegable no debe ofrecer paginas ni articulos. */
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('section_id', 'predictive-search');
      return url.toString();
    }

    search(term) {
      /* Una petición en vuelo a la vez: si el usuario sigue escribiendo, la
         anterior se cancela y no puede pisar a la más reciente. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.showPanel();
      this.skeleton.classList.remove('hidden');
      this.results.innerHTML = '';

      fetch(this.searchUrl(term), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('predictive search ' + response.status);
          return response.text();
        })
        .then((html) => {
          this.skeleton.classList.add('hidden');
          this.results.innerHTML = html;
          this.announce();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.skeleton.classList.add('hidden');
          this.hidePanel();
        });
    }

    announce() {
      if (!this.status) return;
      var count = this.results.querySelectorAll('a[href]').length;
      this.status.textContent = count > 0 ? String(count) : '';
    }

    showPanel() {
      this.panel.removeAttribute('hidden');
    }

    hidePanel() {
      this.panel.setAttribute('hidden', '');
      this.results.innerHTML = '';
      this.skeleton.classList.add('hidden');
      if (this.status) this.status.textContent = '';
    }
  }

  if (!customElements.get('header-search-inline')) {
    customElements.define('header-search-inline', HeaderSearchInline);
  }
})();
